import Button from "components/common/controls/Button";
import Modal from "components/common/controls/modal/Modal";
import PromineoCountDownTimer from "components/common/PromineoCountDownTimer";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import * as routePath from "shared/constants/RoutePathConstants";
import {
  getTokenRefreshConfirmationRemainingTime,
  checkIfTokenRefreshConfirmationShouldBeDisplayed,
} from "shared/utility/AuthenticationTokenHelper";
import {
  userLogout,
  refreshAuthenticationTokenAsync,
} from "store/slices/AuthenticationSlice";

interface Props {
  tokenExpirationTime: string;
}

export default function RefreshTokenConfirmation(props: Props) {
  const [isTimeOut, setIsTimeOut] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const confirmationTimeBeforeExpireInMinute = 5;

  const { tokenExpirationTime } = props;

  useEffect(() => {
    let confirmationPopupDisplayTimeout: any;

    const confirmationShouldBeDisplayedImmediately =
      checkIfTokenRefreshConfirmationShouldBeDisplayed(
        tokenExpirationTime,
        confirmationTimeBeforeExpireInMinute
      );

    if (confirmationShouldBeDisplayedImmediately) {
      setIsConfirmationVisible(true);
    } else {
      setIsConfirmationVisible(false);
      const remainingTime = getTokenRefreshConfirmationRemainingTime(
        tokenExpirationTime,
        confirmationTimeBeforeExpireInMinute
      );

      confirmationPopupDisplayTimeout = setTimeout(() => {
        setIsConfirmationVisible(true);
      }, remainingTime);
    }

    return () => {
      if (confirmationPopupDisplayTimeout) {
        clearTimeout(confirmationPopupDisplayTimeout);
      }
    };
  }, [tokenExpirationTime]);

  const SignoutCompletionDisplay = () => {
    return <div className="font-medium">Please sign in to continue.</div>;
  };

  const getTitle = () => {
    return isTimeOut ? "Session expired" : "Your session is about to expire.";
  };

  const CountDownComponent = () => {
    return (
      <div>
        <PromineoCountDownTimer
          date={moment()
            .add(confirmationTimeBeforeExpireInMinute, "minutes")
            .toString()}
          onComplete={() => setIsTimeOut(true)}
          renderer={(props) => {
            return (
              <>
                <div className="font-medium">You will be signed out in:</div>
                <div className="text-2xl font-bold">
                  <div className="inline-block w-18">
                    {props.formatted.minutes}:{props.formatted.seconds}
                  </div>
                  <div className="inline-block ml-1">mins</div>
                </div>
              </>
            );
          }}
        />
      </div>
    );
  };

  const Content = () => {
    const handleConfirmClick = () => {
      if (isTimeOut) {
        goBackToLoginPage();
      } else {
        dispatch(refreshAuthenticationTokenAsync());
      }
    };

    const goBackToLoginPage = () => {
      dispatch(userLogout());
      history.push(routePath.SIGN_IN);
    };

    return (
      <div>
        {isTimeOut ? <SignoutCompletionDisplay /> : <CountDownComponent />}
        <div className="mt-8 float-right">
          <Button
            text={isTimeOut ? "Back to sign in" : "Stay signed-in"}
            onClick={handleConfirmClick}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        width="450"
        title={getTitle()}
        visible={isConfirmationVisible}
        height="auto"
        displayDefaultActions={false}
      >
        <Content />
      </Modal>
    </>
  );
}
